// Keeps the browser's own chrome (the iPhone status bar area, Safari's tab
// tint, Android's address bar) the same colour as the app's paper, in light
// and dark mode.
//
// Dark mode is a `dark` class on <html> that several places toggle (the
// canvas store, the landing page, dev fixtures). Rather than touch each, one
// observer watches that class and updates <meta name="theme-color"> and the
// root's color-scheme. index.html applies the stored mode before first paint,
// because Safari samples the page colour when it loads.

const META_NAME = 'theme-color';

function paperColor(): string | null {
  const triplet = getComputedStyle(document.documentElement).getPropertyValue('--paper').trim();
  if (!triplet) return null;
  // Stored as "40 20% 96%": write it in the comma form every browser reads
  return `hsl(${triplet.split(/\s+/).join(', ')})`;
}

export function syncThemeColor(): void {
  const root = document.documentElement;
  const dark = root.classList.contains('dark');
  root.style.colorScheme = dark ? 'dark' : 'light';
  const color = paperColor();
  if (!color) return;
  let meta = document.querySelector<HTMLMetaElement>(`meta[name="${META_NAME}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = META_NAME;
    document.head.appendChild(meta);
  }
  if (meta.content !== color) meta.content = color;
  // Home-screen web apps read this at launch; keeping it in step means the next
  // launch matches (dark: page under a light-text status bar; light: white bar)
  const bar = document.querySelector<HTMLMetaElement>('meta[name="apple-mobile-web-app-status-bar-style"]');
  const style = dark ? 'black-translucent' : 'default';
  if (bar && bar.content !== style) bar.content = style;
}

/** Call once at startup. Only the class attribute is observed, and only <meta> and style change, so it cannot loop. */
export function watchThemeColor(): () => void {
  syncThemeColor();
  const observer = new MutationObserver(syncThemeColor);
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  return () => observer.disconnect();
}
